import logEvent from 'analytics/logEvent';
import { getTariff } from 'domains/products/products.utils';
import cn from 'classnames';
import useTranslationAndTypograph from 'hooks/useTranslationAndTypograph';
import { logger } from 'logs';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import qs from 'qs';

import Button from 'components/basic/Button';
import { CircularProgress } from 'components/basic/Loaders';
import PlansInfoTable from 'components/basic/PlansInfoTable';
import { Caption, H2 } from 'components/basic/Typography';
import PaymentForm from 'components/complex/payments/PaymentForm';
import FreeActivationButton from 'components/complex/payments/PaymentForm/FreeActivationButton';
import Product from 'components/complex/payments/Product';
import PromocodeForm from 'components/complex/payments/PromocodeForm';
import Page from 'components/layouts/Page';
import ButtonDescription from 'components/pagesComponents/payments/PlansPage/ButtonDescription';
import WelltoryProLogo from 'components/pagesComponents/payments/PlansPage/WelltoryProLogo';
import { blocksConfig } from 'components/pagesComponents/payments/PlansPage/constants';

import type { AppState } from 'redux-store';
import { useAppDispatch, useAppSelector } from 'redux-store';
import { openModal } from 'redux-store/slices/ui';

import s from './PlansPage.module.scss';

// eslint-disable-next-line complexity
export default function PlansPage({
  pageTitle,
}: {
  pageTitle?: string
}): JSX.Element {
  const t = useTranslationAndTypograph();
  const token = useAppSelector<AppState['jwt']['token']>(
    state => state.jwt.token,
  );
  const productsStore = useAppSelector<AppState['products']>(
    state => state.products,
  );
  const profileStore = useAppSelector<AppState['profile']>(
    state => state.profile,
  );
  const purchase = useAppSelector<
    AppState['purchases']['purchases']['0'] | undefined
  >(state => state.purchases.purchases[0]);

  const [isShowingPaymentForm, setShowingPaymentForm] = useState(false);
  const router = useRouter();
  const dispatch = useAppDispatch();
  
  const coupon = router.query.coupon as string;
  const page = router.query.page as string;
  const selectedIdQuery = router.query.selected as string | undefined;
  const paymentButtonExperiment = router.query.payment_button as
    | string
    | undefined;

  const { token: queryToken, payment_intent_client_secret: paymentIntentClientSecret, ...queryWithoutToken } = router.query;
  const queryString = qs.stringify(queryWithoutToken, { arrayFormat: 'repeat' });

  const utmSource = router.query.utm_source;
  const utmSourceType = router.query.source_type;
  const utmMedium = router.query.utm_medium;
  const utmContent = router.query.utm_content;
  const utmTerm = router.query.utm_term;

  const { isAuthenticated } = profileStore;

  const productsList = productsStore.products;
  const products = productsList;

  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(
    selectedIdQuery ? +selectedIdQuery : null,
  );

  const selectedProduct = selectedIdQuery
    ? products.find((product) => product.id === +selectedIdQuery)
    : undefined;

  const { productPage } = productsStore;
  let config = blocksConfig[productPage];
  const { userAgent } = useAppSelector(state => state.ui);
  const { discount } = productsList[0]?.prices?.[0] || {};
  const current = productsList.find(({ isCurrent }) => isCurrent);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!config) {
    config = blocksConfig.default;
  }

  useEffect(() => {
    logEvent('Web_Payment_Page_Viewed', {
      screen: productsStore.productPage,
      coupon,
      'coupon%': discount,
      viewed_with_token: !!token,
      is_paid: !!current?.is_paid,
      user_authorized: !!token || isAuthenticated,
      product_list: productsList
        .filter(({ is_paid }) => !is_paid)
        .map(({ id }) => id),
      current_tariff: current?.id,
      current_payment_system: purchase?.payment_plan.payment_system,
      utm_source: utmSource,
      source_type: utmSourceType,
      utm_medium: utmMedium,
      utm_content: utmContent,
      utm_term: utmTerm,
      query: queryString,
    });
  }, [coupon, page]);

  useEffect(() => {
    if (selectedIdQuery && selectedProduct) {
      if (isAuthenticated || token) {
        setShowingPaymentForm(true);
      } else {
        dispatch(openModal({ type: 'Auth' }));
      }

      return;
    }

    if (!products.length) {
      const defaultProducts = productsStore.isFetched
        ? productsList.filter(
            ({ isCurrent, is_paid }) => !(isCurrent || is_paid),
          )
        : [];
      const [defaultProduct = { id: -1 }] = defaultProducts;
      let defaultId = defaultProduct.id;

      const yearProduct = defaultProducts.find(
        product => product.prices[0].interval === 'year',
      );

      if (yearProduct) {
        defaultId = yearProduct.id;
      } 
      else if (defaultProducts.length === 1) {
        defaultId = defaultProducts[0]?.id;
      }

      setSelectedPlanId(defaultId);

      return;
    }

    const activeProducts = productsList.filter(
      ({ isCurrent, is_paid }) => !(isCurrent || is_paid),
    );
    const yearProduct = activeProducts.find(
      product => product.prices[0].interval === 'year',
    ) || activeProducts[0];
    const [defaultProduct = { id: -1 }] = activeProducts
      .map(item => ({
        price: item.prices[0].price,
        id: item.id,
      }))
      .sort((a, b) => b.price - a.price);

    setSelectedPlanId(yearProduct?.id || defaultProduct.id);
  }, [productsList]);

  if (!productsStore.isFetched) {
    return <CircularProgress />;
  }

  const productsEls = productsList.map(product => {
    let isCurrentUserPlan = product.isCurrent || product.is_paid;
    if (product.slug === 'welltorygift') {
      isCurrentUserPlan = false;
    }
    return (
      <Product
        setSelectedPlanId={setSelectedPlanId}
        isSelected={selectedPlanId === product.id}
        key={product.id}
        product={product}
        isCurrentUserPlan={isCurrentUserPlan}
        nextPayment={product.nextPayment}
      />
    );
  });
  const selectedPlan = productsList.find(
    product => selectedPlanId === product.id,
  );
  const showPaymentForm = !!paymentButtonExperiment
    ? !!(token || isAuthenticated) &&
      productsList.filter(({ is_paid }) => !is_paid).length === 1 &&
      selectedPlan
    : isShowingPaymentForm && selectedPlan;

  let footer = (
    <div className={s.buttonWrapper}>
      <Button
        look="rainbow"
        className={cn(s.continueButton, 'mt-8 mb-3', 'cy-plans-continue')}
        size="large"
        onClick={(): void => {
          logEvent('Web_Payment_Continue_Button_Clicked', {
            tariff: getTariff(selectedPlan),
            duration: selectedPlan?.prices[0].interval_count,
            coupon,
            'coupon%': discount,
          });
          if (isAuthenticated || token) {
            setShowingPaymentForm(true);
          } else {
            dispatch(openModal({ type: 'Auth' }));
          }
        }}
      >
        {t('continue')}
      </Button>
      <Caption className={s.buttonCaption}>
        <ButtonDescription selectedPlan={selectedPlan} />
      </Caption>
    </div>
  );

  if (showPaymentForm && selectedPlan) {
    footer = (
      <PaymentForm selectedPlan={selectedPlan} />
    );
  }

  if ((isAuthenticated || token) && selectedPlan?.prices[0].discount_price === '0.00') {
    footer = (
      <FreeActivationButton coupon={coupon} selectedPlan={selectedPlan} />
    );
  }

  const title = pageTitle || config.headerText;

  return (
    <Page withoutFooter={productPage === 'promo-special'}>
      <div className={s.bg} />
      <WelltoryProLogo />
      <H2 className={cn(s.h1, 'mb-8', 'cy-plans-title')}>
        {t(title)}
      </H2>
      <div className={s.productWrapper}>{productsEls}</div>

      {config.isPromocodeBlockShowing && page !== 'yearlifetime' && (
        <PromocodeForm selectedPlanTariff={getTariff(selectedPlan)} />
      )}

      {footer}

      {userAgent.device.type !== 'mobile' && config.isLongTableShowing && (
        <>
          <H2 className={cn(s.tableHeader, 'mb-12 mt-12')}>
            {t('Choose the best plan for you')}
          </H2>
          <PlansInfoTable />

          <div className={cn(s.buttonWrapper, s.tableUpgradeButton)}>
            <Button
              className={cn(s.continueButton, 'mt-8 mb-3')}
              size="medium"
              onClick={(): void => {
                window.scrollTo({
                  top: 0,
                  left: 0,
                  behavior: 'smooth',
                });
              }}
            >
              {t('upgrade now')}
            </Button>
          </div>
        </>
      )}
    </Page>
  );
}
